import { CREATE, LOAD, RECONCILE, RESET, UPDATE } from 'model/palIfpEnrolled';
import {
  LOAD_OPERATIONS,
  LOAD_OPERATION_DETAIL,
} from 'model/palIfpEnrolledOps';
import {
  createEnrollment,
  getEnrollmentState,
  reconcileEnrollment,
  resetEnrollment,
  updateDesiredState,
  styleSyncEnrollment,
  getOperations,
  getOperation,
} from 'sdk/palIfpEnrolled';
import { load as loadPalConfig } from 'sdk/fspConfigPayALoan';
import { call, put, select, take, takeLatest } from 'redux-saga/effects';
import { UPDATE as FSP_CONFIG_UPDATE } from 'model/fspConfig';
import { selector as palConfigSelector } from 'selectors/fspConfigPayALoan';
import { selector as fspConfigSelector } from 'selectors/fspConfig';
import { isOn } from 'util/index';
import { SSO_OVERWRITE } from 'consts';
import { equals } from 'ramda';
import { ADD_MESSAGE } from '../util/actions';
import { callAction, ORM } from '../util';

const formatErrorMessage = (errorObj, callData) => {
  const status =
    errorObj?.status ?? errorObj?.statusCode ?? errorObj?.response?.status;
  if (status === 409) {
    return 'Conflict: an operation is already in progress. Please wait and try again.';
  }
  if (status === 412) {
    return 'Version mismatch: the enrollment was updated by another user. Please reload.';
  }

  // BFF errors put data directly on the object; raw Axios errors nest under response.data
  const responseErrors =
    errorObj?.data?.errors ??
    errorObj?.errors ??
    errorObj?.response?.data?.errors;
  if (Array.isArray(responseErrors) && responseErrors.length > 0) {
    if (typeof callData?.onApiErrors === 'function') {
      callData.onApiErrors(responseErrors);
    }
    return responseErrors.map((e) => e.message).join(' | ');
  }

  const message =
    errorObj?.data?.message ?? errorObj?.message ?? errorObj?.error?.message;
  return message || 'An error occurred';
};

const reloadState = callAction({
  api: getEnrollmentState,
  dataName: ORM,
  type: LOAD,
});

const reloadOperationsWorker = callAction({
  api: getOperations,
  dataName: ORM,
  type: LOAD_OPERATIONS,
});

function* reloadOperations(action) {
  const result = yield reloadOperationsWorker(action);
  if (result && typeof action?.data?.onNextToken === 'function') {
    const nextToken = result?.response?.nextToken ?? null;
    action.data.onNextToken(nextToken);
  }
}

function* createEnrollmentAndReload(action) {
  const worker = callAction({
    api: createEnrollment,
    dataName: ORM,
    type: CREATE,
    formatSuccessMessage: () => 'IFP enrollment provisioning started',
    formatErrorMessage,
  });

  const result = yield worker(action);
  if (result) {
    const fspId = action?.data;
    if (fspId) {
      yield reloadState({ data: fspId });
      yield reloadOperations({ data: { fspId } });
    }
  }
}

function* updateAndReload(action) {
  const worker = callAction({
    api: updateDesiredState,
    dataName: ORM,
    type: UPDATE,
    formatSuccessMessage: () => 'IFP enrollment desired state update queued',
    formatErrorMessage,
  });

  const result = yield worker(action);
  if (result) {
    const fspId = action?.data?.fspId;
    if (fspId) {
      yield reloadState({ data: fspId });
      yield reloadOperations({ data: { fspId } });
    }
  }
}

function* reconcileAndReload(action) {
  const worker = callAction({
    api: reconcileEnrollment,
    dataName: ORM,
    type: RECONCILE,
    formatSuccessMessage: () => 'IFP enrollment reconciliation started',
    formatErrorMessage,
  });

  const result = yield worker(action);
  if (result) {
    const fspId = action?.data;
    if (fspId) {
      yield reloadState({ data: fspId });
      yield reloadOperations({ data: { fspId } });
    }
  }
}

// Style-sync only when SSO overwrite theme changed (not i18n/nav/etc.).
const overwriteTheme = (overwrite) => {
  if (overwrite == null) return undefined;
  let parsed = overwrite;
  if (typeof overwrite === 'string') {
    try {
      parsed = JSON.parse(overwrite);
    } catch {
      return undefined;
    }
  }
  return parsed?.theme;
};

export const didSsoThemeChange = (previousOverwrite, nextOverwrite) =>
  !equals(overwriteTheme(previousOverwrite), overwriteTheme(nextOverwrite));

export function* syncEnrolledThemeToCognito(action) {
  const data = action?.data;
  const nextOverwrite = data?.ssoConfig?.[SSO_OVERWRITE];
  const fspId = data?.fspId;
  if (!nextOverwrite || !fspId) return;

  const previous = yield select(fspConfigSelector, fspId);
  if (!didSsoThemeChange(previous?.ssoConfig?.[SSO_OVERWRITE], nextOverwrite)) {
    return;
  }

  const resultAction = yield take((a) => {
    const actionFspId = a?.reduxActionData?.fspId ?? a?.data?.fspId;
    return (
      actionFspId === fspId &&
      (a.type === FSP_CONFIG_UPDATE.SUCCESS ||
        a.type === FSP_CONFIG_UPDATE.FAILURE)
    );
  });
  if (resultAction.type === FSP_CONFIG_UPDATE.FAILURE) {
    return;
  }

  let palConfig = yield select(palConfigSelector, fspId);
  if (palConfig?.enrolledUserOffOn == null) {
    try {
      const loaded = yield call(loadPalConfig, fspId);
      palConfig = Array.isArray(loaded) ? loaded[loaded.length - 1] : loaded;
    } catch {
      return;
    }
  }
  if (!isOn(palConfig?.enrolledUserOffOn)) return;

  let result;
  try {
    result = yield call(styleSyncEnrollment, { fspId, reason: 'SSO_SAVE' });
  } catch (error) {
    const status =
      error?.status ?? error?.statusCode ?? error?.response?.status;
    if (status === 409) {
      yield put({
        type: ADD_MESSAGE.ACTION,
        data: 'Cognito style sync deferred until the current operation completes.',
      });
    } else if (status === 400) {
      const responseErrors =
        error?.data?.errors ?? error?.errors ?? error?.response?.data?.errors;
      const detail = (responseErrors || [])
        .map((e) => e?.code || e?.message)
        .filter(Boolean)
        .join(', ');
      yield put({
        type: ADD_MESSAGE.ACTION,
        data: `Cognito style sync validation failed${
          detail ? `: ${detail}` : ''
        }.`,
      });
    }
    // Best-effort on save; a transient failure should not disrupt the SSO save.
    return;
  }

  if (result?.outcome === 'QUEUED') {
    yield put({
      type: ADD_MESSAGE.ACTION,
      data: 'Cognito login style sync queued.',
    });
    yield reloadState({ data: fspId });
    yield reloadOperations({ data: { fspId } });
  }
  // NO_CHANGES / other outcomes => no-op
}

export default function* saga() {
  yield takeLatest(CREATE.ACTION, createEnrollmentAndReload);

  yield takeLatest(LOAD.ACTION, reloadState);
  yield takeLatest(
    RESET.ACTION,
    callAction({
      api: resetEnrollment,
      dataName: ORM,
      type: RESET,
    })
  );

  yield takeLatest(UPDATE.ACTION, updateAndReload);

  yield takeLatest(RECONCILE.ACTION, reconcileAndReload);

  yield takeLatest(FSP_CONFIG_UPDATE.ACTION, syncEnrolledThemeToCognito);

  yield takeLatest(LOAD_OPERATIONS.ACTION, reloadOperations);

  yield takeLatest(
    LOAD_OPERATION_DETAIL.ACTION,
    callAction({
      api: getOperation,
      dataName: ORM,
      type: LOAD_OPERATION_DETAIL,
    })
  );
}
